import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ReportsTable } from '../ReportGroup';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import ReportProgramDetails from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt/ReportProgramDetails';
import { mapReportToAnswers } from './utils';
import ReportDescription from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt/ReportDescription';
import { ImpactModelHeading } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt';
import ReportImpactSummaryTable from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt/ReportImpactSummary';
import ReportBudget from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt/ReportBudget';
import { RenderBeneficiaries } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt/ReportBeneficiaries';
import ReportNarrative from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt/ReportNarrative';
import ReportRawValues from './ReportRawValues';
// eslint-disable-next-line max-len
import ReportBeneficiariesRow from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt/ReportBeneficiaries/ReportBeneficiariesRow';
import ReportActivities from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt/ReportActivities';
import ReportSectionNote from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt/ReportSectionNote';
import { NOTE_QUESTION_IDS } from '../../ReportBuilder/constants';
import { useReport } from 'shared/providers/ReportProvider';
import LegacyReportInfo from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt/LegacyReportInfo';

const getBeneficiaries = (
  beneficiaries: ReportsTable['beneficiaryDemographics'],
) => {
  const result: JSX.Element[] = [];
  for (const b of beneficiaries) {
    result.push(
      <ReportBeneficiariesRow
        category={b.category}
        groupName={b.bdtName}
        subGroupName={b.description}
        percentage={b.percentage}
      />,
    );
  }
  return result;
};

interface ReportReceiptProps {
  programName: string | undefined;
  report: ReportsTable;
  showReportStatus?: boolean;
}

interface OldReportProps extends Omit<ReportReceiptProps, 'programName'> {
  handleClose: () => unknown;
}

const StyledDiv = styled.div``;

export const ReportReceipt: React.FC<ReportReceiptProps> = ({
  report,
  programName,
  showReportStatus = false,
}) => {
  const { loadReport } = useReport();

  useEffect(() => {
    loadReport(report.dbId);
  }, [report.dbId, loadReport]);

  const answers = useMemo(() => mapReportToAnswers(report), [report]);
  const beneficiaries = getBeneficiaries(report.beneficiaryDemographics);
  return (
    <StyledDiv>
      <ReportProgramDetails
        organizationName={undefined}
        programName={report.name}
        isThirdParty={false}
        reportStatus={showReportStatus ? report.status : undefined}
      />
      <ReportDescription
        defaultDescription={report.reportDescription}
        answers={answers}
      />
      <ReportActivities answers={answers} />
      <ImpactModelHeading />
      <ReportImpactSummaryTable
        reportId={report.dbId}
        programName={programName}
        answers={answers}
        isLegacy={true}
      />
      <ReportSectionNote
        answers={answers}
        questionId={NOTE_QUESTION_IDS.impactDetails}
        label="Impact Model notes:"
      />
      <RenderBeneficiaries answers={answers} beneficiaries={beneficiaries} />
      <ReportNarrative answers={answers} />
      <ReportBudget reportId={report.dbId} answers={answers} isLegacy={true} />
      <LegacyReportInfo report={report} showInterventionCategories={false} />

      <ReportRawValues report={report} />
    </StyledDiv>
  );
};

const OldReport: React.FC<OldReportProps> = ({ report, handleClose }) => {
  return (
    <TrueImpactModal
      open={true}
      hideFooter={true}
      showPrintButton={true}
      showExpandButton={true}
      onClose={handleClose}
      maxWidth="1300px"
    >
      <ReportReceipt programName={report.program?.name} report={report} />
    </TrueImpactModal>
  );
};

export default OldReport;
